import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Trash2, Copy, Info, X } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const PAGE_SIZE = 48;

const ENTITY_LABELS = {
    cake: "Gallery Cake",
    flavour: "Flavour",
    blog: "Blog Post",
    category: "Gallery Category",
    shop_category: "Shop Category",
    product: "Shop Product",
    setting: "Site Setting",
};

const ENTITY_ROUTES = {
    cake: "/admin/cakes",
    flavour: "/admin/flavours",
    blog: "/admin/blogs",
    category: "/admin/categories",
    shop_category: "/admin/shop-categories",
    product: "/admin/products",
    setting: "/admin/settings",
};

function UsagesModal({ file, onClose }) {
    const [usages, setUsages] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/admin/files/${file.id}/usages`)
            .then((r) => setUsages(r.data.usages || []))
            .catch(() => toast.error("Failed to check usages"))
            .finally(() => setLoading(false));
    }, [file.id]);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 100 }} onClick={onClose}>
            <div className="bg-white w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-[#EAE3DE]">
                    <h3 className="font-display text-lg">Where this image is used</h3>
                    <button type="button" onClick={onClose} aria-label="Close"><X size={18} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <p className="text-sm text-ink-soft">Checking…</p>
                    ) : usages.length === 0 ? (
                        <p className="text-sm text-ink-soft">Not currently used anywhere on the site — safe to delete.</p>
                    ) : (
                        <ul className="space-y-3">
                            {usages.map((u, i) => (
                                <li key={i} className="text-sm">
                                    <Link to={ENTITY_ROUTES[u.type] || "/admin"} className="text-ink hover:text-blush" onClick={onClose}>
                                        <span className="text-[10px] tracking-[0.2em] uppercase text-blush block">
                                            {ENTITY_LABELS[u.type] || u.type}
                                        </span>
                                        {u.name || "(untitled)"}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function AdminMediaLibrary() {
    const [files, setFiles] = useState([]);
    const [total, setTotal] = useState(0);
    const [skip, setSkip] = useState(0);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [usagesFile, setUsagesFile] = useState(null);

    const load = (nextSkip = skip, q = search) => {
        setLoading(true);
        api.get("/admin/files", { params: { skip: nextSkip, limit: PAGE_SIZE, search: q } })
            .then((r) => {
                setFiles(r.data.files || []);
                setTotal(r.data.total || 0);
                setSkip(nextSkip);
            })
            .catch(() => toast.error("Failed to load media library"))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(0, ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onSearchSubmit = (e) => {
        e.preventDefault();
        load(0, search);
    };

    const remove = async (file) => {
        let usages = [];
        try {
            const r = await api.get(`/admin/files/${file.id}/usages`);
            usages = r.data.usages || [];
        } catch {
            // If the usage check fails, fall through to the plain confirm below.
        }
        const warning = usages.length
            ? `This image is used in ${usages.length} place${usages.length > 1 ? "s" : ""} (${usages.map((u) => u.name || u.type).join(", ")}). Deleting it will break those images. `
            : "";
        if (!window.confirm(`${warning}Remove "${file.original_filename || file.storage_path}" from the library?`)) return;
        try {
            await api.delete(`/admin/files/${file.id}`);
            toast.success("Removed");
            load(skip, search);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to remove");
        }
    };

    const copyUrl = (absolute) => {
        navigator.clipboard?.writeText(absolute);
        toast.success("URL copied");
    };

    const hasPrev = skip > 0;
    const hasNext = skip + PAGE_SIZE < total;

    return (
        <div data-testid="admin-media-library">
            <div className="mb-6">
                <h2 className="font-display text-3xl mb-1">Media Library</h2>
                <p className="text-sm text-ink-soft">
                    Every image ever uploaded across the site. Delete unused files, or copy a URL to reuse elsewhere.
                </p>
            </div>

            <form onSubmit={onSearchSubmit} className="flex gap-2 mb-6 max-w-md">
                <input
                    type="text"
                    placeholder="Search filenames…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="flex-1 px-4 py-3 border border-[#EAE3DE] focus:outline-none focus:border-blush text-sm"
                    data-testid="media-search-input"
                />
                <button type="submit" className="btn-pink inline-flex items-center gap-2">
                    <Search size={14} /> Search
                </button>
            </form>

            {loading ? (
                <p className="text-sm text-ink-soft">Loading…</p>
            ) : files.length === 0 ? (
                <p className="text-sm text-ink-soft">No images found.</p>
            ) : (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {files.map((f) => {
                            const absolute = f.url.startsWith("http") ? f.url : `${BACKEND_URL}${f.url}`;
                            return (
                                <div key={f.id} className="bg-white border border-[#EAE3DE] group" data-testid={`media-item-${f.id}`}>
                                    <div className="relative aspect-square overflow-hidden bg-[#F9F5F2]">
                                        <img src={absolute} alt={f.original_filename || ""} className="w-full h-full object-cover" loading="lazy" />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                            <button
                                                type="button"
                                                onClick={() => setUsagesFile(f)}
                                                className="w-8 h-8 bg-white text-ink flex items-center justify-center hover:bg-blush hover:text-white"
                                                aria-label="Where is this used?"
                                                data-testid={`media-item-usages-${f.id}`}
                                            >
                                                <Info size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => copyUrl(absolute)}
                                                className="w-8 h-8 bg-white text-ink flex items-center justify-center hover:bg-blush hover:text-white"
                                                aria-label="Copy URL"
                                            >
                                                <Copy size={14} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => remove(f)}
                                                className="w-8 h-8 bg-white text-ink flex items-center justify-center hover:bg-red-500 hover:text-white"
                                                aria-label="Delete"
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <p className="px-2 py-2 text-[10px] text-ink-soft truncate" title={f.original_filename}>
                                        {f.original_filename || f.storage_path.split("/").pop()}
                                    </p>
                                </div>
                            );
                        })}
                    </div>

                    <div className="flex items-center justify-between mt-6">
                        <p className="text-xs text-ink-soft">
                            Showing {skip + 1}–{Math.min(skip + PAGE_SIZE, total)} of {total}
                        </p>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={!hasPrev}
                                onClick={() => load(Math.max(0, skip - PAGE_SIZE), search)}
                                className="px-4 py-2 border border-[#EAE3DE] text-xs uppercase tracking-[0.2em] disabled:opacity-40"
                            >
                                Prev
                            </button>
                            <button
                                type="button"
                                disabled={!hasNext}
                                onClick={() => load(skip + PAGE_SIZE, search)}
                                className="px-4 py-2 border border-[#EAE3DE] text-xs uppercase tracking-[0.2em] disabled:opacity-40"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}

            {usagesFile && <UsagesModal file={usagesFile} onClose={() => setUsagesFile(null)} />}
        </div>
    );
}
